/**
 * Crescent — i18n context, language picker, TTS player, exit-intent modal.
 *
 * Design:
 *   • <CrescentI18nProvider> wraps the layout. Reads the active language
 *     from localStorage. First-time visitors see a full-page selector.
 *   • `useCrescentT()` hook: pass any JSON content (string / object / array),
 *     get a translated copy. Cache in-memory for the session + backend DB.
 *   • <LanguageSwitcher> dropdown lives in the header.
 *   • <TTSPlayer> appears on every content page's hero.
 *   • <ExitIntentModal> fires once per session when mouse leaves viewport.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Globe, Check, Play, Pause, Loader2, Volume2, X, Mail, ArrowRight, Headphones,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_LANG = "crescent.lang";
const STORAGE_PICKED = "crescent.langPicked";
const STORAGE_EXIT_SHOWN = "crescent.exitShown";

const Ctx = createContext(null);

export function useCrescentLang() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCrescentLang must be used inside <CrescentI18nProvider>");
  return ctx;
}

/** Translate content using the backend; caches locally per session. */
export function useCrescentT(content) {
  const { lang, translate } = useCrescentLang();
  const [translated, setTranslated] = useState(content);
  const [state, setState] = useState("idle");

  useEffect(() => {
    if (!content || lang === "en") { setTranslated(content); setState("idle"); return; }
    let cancelled = false;
    setState("loading");
    translate(content)
      .then((out) => { if (!cancelled) { setTranslated(out); setState("done"); } })
      .catch(() => { if (!cancelled) { setTranslated(content); setState("error"); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, lang]);

  return { t: translated, state };
}

export function CrescentI18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_LANG) || "en");
  const [picked, setPicked] = useState(() => localStorage.getItem(STORAGE_PICKED) === "1");
  const [languages, setLanguages] = useState({ pre_built: [], runtime: [] });
  const cacheRef = useRef(new Map()); // key: `${lang}:${hash}` → translated

  useEffect(() => {
    axios.get(`${API}/crescent/i18n/languages`).then((r) => setLanguages(r.data)).catch(() => {});
  }, []);

  const allLangs = useMemo(() => [...languages.pre_built, ...languages.runtime], [languages]);
  const activeMeta = allLangs.find((l) => l.code === lang) || { code: "en", name: "English", native: "English", rtl: false };

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", activeMeta.rtl ? "rtl" : "ltr");
  }, [lang, activeMeta.rtl]);

  const setLang = (code) => {
    localStorage.setItem(STORAGE_LANG, code);
    localStorage.setItem(STORAGE_PICKED, "1");
    setLangState(code);
    setPicked(true);
  };

  const translate = async (content) => {
    if (lang === "en") return content;
    const key = `${lang}:${hashContent(content)}`;
    if (cacheRef.current.has(key)) return cacheRef.current.get(key);
    const { data } = await axios.post(`${API}/crescent/i18n/translate`, { content, lang });
    cacheRef.current.set(key, data.translated);
    return data.translated;
  };

  const value = { lang, setLang, picked, setPicked, languages, allLangs, activeMeta, translate };
  return (
    <Ctx.Provider value={value}>
      {children}
      {!picked && <LanguageSelectorModal onClose={() => setPicked(true)} />}
    </Ctx.Provider>
  );
}

function hashContent(obj) {
  const s = JSON.stringify(obj, Object.keys(obj || {}).sort());
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0; }
  return `${h}`;
}

// =============================================================================
// Full-page selector (first visit)
// =============================================================================
function LanguageSelectorModal({ onClose }) {
  const { setLang, languages } = useCrescentLang();
  return (
    <div className="fixed inset-0 z-[90] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="language-selector-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full p-8 relative">
        <button onClick={onClose} data-testid="lang-skip" className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X size={20} /></button>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Globe size={22} /></div>
          <div>
            <h2 className="font-heading font-extrabold text-slate-900 text-2xl">Choose your language</h2>
            <p className="text-sm text-slate-500">You can change this anytime from the header.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="lang-pre-built">
          {languages.pre_built.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); onClose(); }}
              data-testid={`lang-pick-${l.code}`}
              className="group flex items-center gap-3 border border-slate-200 rounded-xl p-3 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-left"
            >
              <span className="text-2xl">{l.flag}</span>
              <span>
                <div className="font-semibold text-slate-900 text-sm">{l.native}</div>
                <div className="text-xs text-slate-500">{l.name}</div>
              </span>
            </button>
          ))}
        </div>
        <details className="mt-5">
          <summary className="text-xs font-semibold text-emerald-700 cursor-pointer">Other languages (translated on demand) →</summary>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="lang-runtime">
            {languages.runtime.map((l) => (
              <button key={l.code} onClick={() => { setLang(l.code); onClose(); }} className="text-sm text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-400">
                {l.native} <span className="text-xs text-slate-400">{l.code}</span>
              </button>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

// =============================================================================
// Header dropdown
// =============================================================================
export function LanguageSwitcher() {
  const { lang, setLang, allLangs, languages, activeMeta } = useCrescentLang();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = () => setOpen(false);
    if (open) { window.addEventListener("click", close); return () => window.removeEventListener("click", close); }
  }, [open]);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="lang-switcher"
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-700 transition-colors"
      >
        <Globe size={13} /> {activeMeta.flag || "🌐"} {activeMeta.native || "English"}
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg py-2 z-50 max-h-[70vh] overflow-y-auto" data-testid="lang-dropdown">
          <div className="px-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pre-translated</div>
          {languages.pre_built.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }} data-testid={`lang-select-${l.code}`}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-emerald-50 text-left">
              <span>{l.flag}</span><span className="flex-1">{l.native}</span>
              {lang === l.code && <Check size={14} className="text-emerald-600" />}
            </button>
          ))}
          <div className="px-3 pt-2 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Runtime translation</div>
          {languages.runtime.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1 text-sm hover:bg-emerald-50 text-left">
              <span className="w-5 text-[10px] text-slate-400">{l.code}</span>
              <span className="flex-1">{l.native}</span>
              {lang === l.code && <Check size={14} className="text-emerald-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TTS Player
// =============================================================================
export function TTSPlayer({ text, label = "Listen to page" }) {
  const { lang } = useCrescentLang();
  const [state, setState] = useState("idle"); // idle | loading | ready | playing | error
  const [audio, setAudio] = useState(null);

  useEffect(() => () => { if (audio) { audio.pause(); } }, [audio]);
  useEffect(() => { setState("idle"); setAudio(null); }, [text, lang]);

  const prepareAndPlay = async () => {
    if (state === "playing") { audio?.pause(); setState("ready"); return; }
    if (audio && state === "ready") { audio.play(); setState("playing"); return; }
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/crescent/i18n/tts`, { text, voice: "alloy", lang });
      const a = new Audio(data.audio_data_url);
      a.onended = () => setState("ready");
      a.onerror = () => setState("error");
      setAudio(a);
      a.play(); setState("playing");
    } catch (e) {
      setState("error");
      toast.error("Audio unavailable right now");
    }
  };

  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Play;
  return (
    <button
      type="button"
      onClick={prepareAndPlay}
      data-testid="tts-player"
      className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-semibold px-4 py-2 rounded-full text-sm transition-colors backdrop-blur-sm"
    >
      <Icon size={14} className={state === "loading" ? "animate-spin" : ""} />
      <Headphones size={13} className="opacity-70" />
      {state === "playing" ? "Pause" : state === "loading" ? "Preparing audio…" : label}
    </button>
  );
}

// =============================================================================
// Exit-intent modal — one-field RFQ nudge
// =============================================================================
export function ExitIntentModal() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_EXIT_SHOWN) === "1") return;
    const handler = (e) => {
      if (e.clientY <= 10 && !visible) {
        setVisible(true);
        localStorage.setItem(STORAGE_EXIT_SHOWN, "1");
      }
    };
    const timer = setTimeout(() => { document.addEventListener("mouseleave", handler); }, 4000);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", handler); };
  }, [visible]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { toast.error("Please share a valid email"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/crescent/exit-intent`, { email, note, path: window.location.pathname });
      setDone(true);
      toast.success("Thank you — our team will reach out shortly.");
    } catch {
      toast.error("Something went wrong, please try again.");
    }
    setSubmitting(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="exit-intent-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 relative">
        <button onClick={() => setVisible(false)} data-testid="exit-close" className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X size={20} /></button>
        {done ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto"><Check size={28} /></div>
            <h3 className="font-heading font-extrabold text-slate-900 text-xl mt-4">Captured!</h3>
            <p className="text-slate-600 mt-2 text-sm">Our packaging team will be in touch within one working day.</p>
            <button onClick={() => setVisible(false)} className="mt-5 bg-emerald-600 text-white font-bold px-5 py-2 rounded-full text-sm">Continue browsing</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Mail size={22} /></div>
              <div>
                <h3 className="font-heading font-extrabold text-slate-900 text-xl">Before you go…</h3>
                <p className="text-xs text-slate-500">Drop your email — a packaging expert will send structure + MOQ suggestions for your product.</p>
              </div>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input
                type="email"
                data-testid="exit-email"
                placeholder="you@brand.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:border-emerald-500 outline-none"
              />
              <input
                type="text"
                data-testid="exit-note"
                placeholder="(optional) what are you packing?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-2 text-xs focus:border-emerald-500 outline-none"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="exit-submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-xl text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="animate-spin" size={14} /> : <ArrowRight size={14} />}
                Send me packaging suggestions
              </button>
            </form>
            <p className="text-[10px] text-slate-400 text-center mt-3">We only use your email to reply to your enquiry.</p>
          </>
        )}
      </div>
    </div>
  );
}
